import { type GQL, Utils } from "@binale-tech/shared";
import { Button } from "@heroui/react";
import dayjs from "dayjs";
import React, { type FC, memo, use, useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { useAiLogSource } from "@dms/components/Bewirtung/aiLogSource";
import { type AssistantActions } from "@dms/components/DmsSortAssistant/useAssistantActions";
import { BankTransactionsContext } from "@domain/context/BanksContext";

import { ANSWER_CLASS, LaterButton, QUESTION_CLASS, type Run } from "./panelShared";

type Props = {
    documentId: string;
    companyId: string | undefined;
    candidateTransactionIds: string[];
    isPending: boolean;
    run: Run;
    onNoneOfThem: () => void;
    onLater?: () => void;
    linkAndMoveToBank: AssistantActions["linkAndMoveToBank"];
};

const formatBookingDate = (date: string): string => {
    const parsed = dayjs(date);
    return parsed.isValid() ? parsed.format("DD.MM.YYYY") : date;
};

type CandidateButtonProps = {
    transaction: GQL.IBankingTransaction;
    isPending: boolean;
    onPick: (transactionId: string) => void;
};

const CandidateButtonImpl: FC<CandidateButtonProps> = ({ transaction, isPending, onPick }) => {
    const handlePress = useCallback(() => onPick(transaction.id), [onPick, transaction.id]);
    return (
        <Button
            className="h-auto w-full flex-col items-start py-2 text-left"
            variant="outline"
            isDisabled={isPending}
            onPress={handlePress}
        >
            <span className="flex w-full justify-between gap-2 font-semibold">
                <span>{formatBookingDate(transaction.bookingDate)}</span>
                <span className="tabular-nums">
                    {Utils.CurrencyUtils.currencyFormat(transaction.amount)} {transaction.currencyCode}
                </span>
            </span>
            {transaction.counterpartyName && <span>{transaction.counterpartyName}</span>}
            {transaction.purpose && <span className="text-sm text-muted whitespace-normal">{transaction.purpose}</span>}
        </Button>
    );
};

const CandidateButton = memo(CandidateButtonImpl);

const BankPickPanelImpl: FC<Props> = ({
    documentId,
    companyId,
    candidateTransactionIds,
    isPending,
    run,
    onNoneOfThem,
    onLater,
    linkAndMoveToBank,
}) => {
    const { transactionMap } = use(BankTransactionsContext);
    const candidates = useMemo(
        () =>
            candidateTransactionIds
                .map((id) => transactionMap.get(id))
                .filter((t): t is GQL.IBankingTransaction => !!t),
        [candidateTransactionIds, transactionMap],
    );
    const { source, isLoading } = useAiLogSource(documentId, companyId);
    const aiPaymentMethod = source?.payment_method ?? null;
    const handlePick = useCallback(
        (transactionId: string) => run(() => linkAndMoveToBank(documentId, transactionId, aiPaymentMethod)),
        [aiPaymentMethod, documentId, linkAndMoveToBank, run],
    );

    return (
        <div className="flex flex-col gap-3">
            <h3 className={QUESTION_CLASS}>
                <FormattedMessage id="app.dms.sortAssistant.bankPickQuestion" />
            </h3>
            {candidates.map((transaction) => (
                <CandidateButton
                    key={transaction.id}
                    transaction={transaction}
                    isPending={isPending || isLoading}
                    onPick={handlePick}
                />
            ))}
            <Button className={ANSWER_CLASS} variant="outline" isDisabled={isPending} onPress={onNoneOfThem}>
                <FormattedMessage id="app.dms.sortAssistant.noneOfThem" />
            </Button>
            <LaterButton isPending={isPending} onLater={onLater} />
        </div>
    );
};

export const BankPickPanel = memo(BankPickPanelImpl);
